import { createFileRoute } from "@tanstack/react-router";
import {
  LiveRatesTable,
  LiveChart,
  DeviationBadge,
  KaratWeightCalc,
  KsaLinkMatrix,
  FaqAccordion,
  buildKsaSchema,
  arDateParts,
  goldRatesQueryOptions,
} from "@/components/sa-ar/shell";
import { computeSaPrices, formatSar, formatSarInt } from "@/lib/sa-gold/data";
import { useGoldRates } from "@/lib/nonblocking-rates";

const SLUG = "/sa/ar/gold-bars-bullions";
const CANONICAL = `https://drhint.com${SLUG}`;

const FAQS = [
  { q: "كم سعر سبيكة الذهب 10 جرام عيار 24 في السعودية اليوم؟", a: "سعر سبيكة 10 جرام عيار 24 يعادل سعر جرام الذهب الخالص × 10 + قسط دار المسكوكات (2–4% حسب حجم السبيكة والمصدّر). القيمة الفورية موضحة في المصفوفة أعلاه." },
  { q: "كم سعر أونصة الذهب بالريال السعودي في تعاملات اليوم؟", a: "أونصة الذهب = 31.1035 جراماً. سعر الأونصة بالريال = سعر الأونصة بالدولار × 3.75 (الربط الثابت للريال). القيمة اللحظية معروضة في جدول الأسعار أعلاه." },
  { q: "كيف يتم تحديث أسعار سبائك الذهب لشركة BTC في السعودية؟", a: "الشركات المتخصصة مثل BTC والراجحي والدرة تحدّث أسعار السبائك يومياً عند فتح السوق، بناءً على سعر السبائك العالمي في لندن (LBMA) بعد إضافة قسط الصك (2–4%) وتغليف السبيكة الرسمي." },
  { q: "ما هو أفضل عيار ذهب للاستثمار والحفاظ على رأس المال في السعودية؟", a: "عيار 24 (999) هو المعيار الاستثماري لأنه معفى من ضريبة القيمة المضافة، ولا يحتاج أجرة صياغة، وسعر بيعه أقرب ما يكون إلى سعر السوق الفوري. السبائك الرسمية 100 جرام و1 كيلو تحقق أفضل هامش شراء/بيع." },
];

export const Route = createFileRoute("/sa/ar/gold-bars-bullions")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    return null;
  },
  head: () => {
    const title = `أسعار سبائك الذهب اليوم في السعودية بجميع الأوزان`;
    const description = `مصفوفة مقارنة أسعار سبائك الذهب النقي عيار 24 في السعودية: 1، 5، 10، 20، 50، 100 جرام و1 كيلو بالريال السعودي، شاملة قسط دار السك والمسكوكات.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "ar-SA" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "ar_SA" },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "ar-SA", href: CANONICAL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildKsaSchema({ slug: SLUG, name: title, description, faqs: FAQS })),
        },
      ],
    };
  },
  component: BullionPage,
});

function BullionPage() {
  const dp = arDateParts();
  return (
    <main dir="rtl" className="min-h-screen bg-background text-foreground">
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500">KSA · السبائك والاستثمار</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-black leading-tight">
          أسعار سبائك الذهب اليوم في السعودية بجميع الأوزان — {dp.day} {dp.dayNum} {dp.month} {dp.year}
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-3xl">
          دليل أوزان السبائك المتوفرة رسمياً في السوق السعودي: 1 جرام، 5، 10، 20، 50، 100 جرام و1 كيلوجرام. الأسعار تشمل قسط دار السك (2–4%) وتُحدّث مع سعر البورصة العالمية.
        </p>
        <div className="mt-3"><DeviationBadge /></div>
      </header>
      <LiveChart title={`منحنى سعر السبيكة (عيار 24) — ${dp.month} ${dp.year}`} />
      <LiveRatesTable />
      <BullionMatrix />
      <MintPremiumTable />
      <KaratWeightCalc />
      <FaqAccordion items={FAQS} />
      <KsaLinkMatrix current={SLUG} />
    </main>
  );
}

function BullionMatrix() {
  const { data } = useGoldRates();
  const base24 = computeSaPrices({ spotUsdPerOz: data.spotUsdPerOz })[24];
  const weights = [1, 5, 10, 20, 50, 100, 250, 500, 1000];
  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <h2 className="text-xl md:text-2xl font-bold mb-2">مصفوفة مقارنة سبائك الذهب النقي (عيار 24) بالوزن</h2>
      <p className="text-sm text-muted-foreground mb-4">
        السعر الأساسي (سعر الجرام × الوزن) + تقدير قسط دار السك (Premium) الذي يقل مع كبر الوزن، ثم السعر النهائي للسبيكة.
      </p>
      <div className="overflow-hidden rounded-xl border border-amber-500/30 bg-card">
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-amber-500/10 text-right">
            <tr>
              <th className="px-3 py-2">الوزن</th>
              <th className="px-3 py-2 text-left">السعر الأساسي</th>
              <th className="px-3 py-2 text-left">قسط السك</th>
              <th className="px-3 py-2 text-left">السعر النهائي</th>
            </tr>
          </thead>
          <tbody>
            {weights.map((w) => {
              const base = base24 * w;
              const premiumPct = w >= 1000 ? 0.015 : w >= 100 ? 0.02 : w >= 20 ? 0.028 : w >= 5 ? 0.035 : 0.05;
              const premium = base * premiumPct;
              return (
                <tr key={w} className="border-t border-amber-500/10">
                  <th className="px-3 py-2 text-right font-medium">{w >= 1000 ? "1 كيلو" : `${w} جرام`}</th>
                  <td className="px-3 py-2 text-left">{formatSarInt(base)}</td>
                  <td className="px-3 py-2 text-left text-amber-600 dark:text-amber-300">+ {formatSarInt(premium)} ({(premiumPct * 100).toFixed(1)}%)</td>
                  <td className="px-3 py-2 text-left font-bold">{formatSarInt(base + premium)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MintPremiumTable() {
  const mints = [
    { name: "BTC للسبائك (السعودية)", premium: "2.0% – 3.0%", notes: "قسط تنافسي على أوزان 100 جرام فأكثر" },
    { name: "الراجحي — الذهب الاستثماري", premium: "2.5% – 3.5%", notes: "شهادات موثوقة، شراء وبيع فوري" },
    { name: "الدرة للمسكوكات", premium: "2.2% – 3.2%", notes: "سبائك مختومة رسمياً بأوزان صغيرة ومتوسطة" },
    { name: "PAMP السويسرية (المستوردة)", premium: "3.0% – 4.5%", notes: "علامة عالمية، هامش أعلى بسبب الاستيراد" },
  ];
  return (
    <section className="mx-auto max-w-6xl px-4 pb-8">
      <h2 className="text-lg md:text-xl font-bold mb-3">أشهر مصدّري السبائك في السوق السعودي وأقساطهم</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {mints.map((m) => (
          <div key={m.name} className="rounded-2xl border border-border bg-card p-4">
            <div className="font-semibold">{m.name}</div>
            <div className="mt-1 text-sm text-amber-600 dark:text-amber-300 tabular-nums">القسط: {m.premium}</div>
            <div className="mt-1 text-xs text-muted-foreground">{m.notes}</div>
          </div>
        ))}
      </div>
    </section>
  );
}